// MapComponent.tsx
import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../modal/Modal'
import { SuperAdminInsertMachinesPopup } from '@/pages/dashboard/superadmin/machines/Machines'

import 'leaflet/dist/leaflet.css'
import './HitMap.css'
import { HIG_HIT, LOW_HIT, MID_HIT } from '@/services/repository/hits'
import { formatDate } from '@/common/time'
import { Roles } from '@/services/repository/users'
import { defaultMapCenter } from '@/env/config'

interface MapRowMachine {
  id: string | undefined
  name: string | undefined
}

interface MapRowParams {
  hitX: number
  hitY: number
  hitZ: number
  ucid: string
}

interface MapRow {
  imei: string
  time: string // ISO 8601 string representation of DTTime (e.g., "2025-01-17T12:34:56Z")
  lat: number  // Latitude as a floating-point number
  lng: number  // Longitude as a floating-point number
  altitude: number // Altitude in meters
  angle: number // Angle in degrees
  speed: number // Speed in meters per second
  machine: MapRowMachine
  params: MapRowParams
}

interface MapResponse {
  time: string // ISO 8601 string representation of the latest DtServer
  rows: MapRow[] | null   // Array of MapRow objects
}

export function mergeMapResponses(oldResponse: MapResponse, newResponse: MapResponse): MapResponse {
  if (!newResponse.rows) {
    return oldResponse
  }
  const updatedRows: MapRow[] = oldResponse.rows ? [...oldResponse.rows] : []
  const imeiMap = new Map<string, MapRow>(updatedRows.map(row => [row.imei, row]))
  for (const newRow of newResponse.rows) {
    imeiMap.set(newRow.imei, newRow)
  }
  const mergedRows = Array.from(imeiMap.values());
  return {
    time: newResponse.time,
    rows: mergedRows,
  }
}


export const HitMapComponent = ({ services, session, fetchUrl }: {
  services: Services,
  session: CurrentSession
  fetchUrl: string
}) => {
  const [map, setMap] = useState<MapResponse | undefined>(undefined)
  const navigate = useNavigate()
  const { openModal } = useModal()
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)

  async function refreshMap(fromDate?: Date, oldResponse?: MapResponse): Promise<MapResponse> {
    return services.session.fetchWithToken(`${fetchUrl}?fromDate=${fromDate ? fromDate.toISOString() : ''}`).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<MapResponse>()
      }
      const newResponse: MapResponse = await r.json()
      newResponse.rows = newResponse.rows || null
      if (oldResponse === undefined) {
        return newResponse
      }
      return mergeMapResponses(oldResponse, newResponse)
    })
  }

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  useEffect(() => {
    refreshMap().then(setMap)
  }, []);

  // useEffect(() => {
  //   if (map === undefined) {
  //     return
  //   }
  //   const clock = async () => {
  //     if (map === undefined) {
  //       return
  //     }
  //     await refreshMap(map?.time ? new Date(map?.time) : undefined, map).then(setMap)
  //     handle = setTimeout(clock, 2000)
  //   }
  //   let handle: NodeJS.Timeout = setTimeout(clock, 8000)
  //   return () => {
  //     clearTimeout(handle)
  //   }
  // }, [map])

  if (map === undefined) {
    return <div className='spinner'></div>
  }


  function findLatestRow(response: MapResponse | undefined): MapRow | undefined {
    if (!response?.rows || response.rows.length === 0) {
      return undefined;
    }

    return response.rows.reduce((latest, current) => {
      return new Date(current.time) > new Date(latest.time) ? current : latest;
    });
  }

  const firstRow = findLatestRow(map);

  const center: LatLngExpression = (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || myposition || defaultMapCenter

  function hitClassColor(value: number): string {
    value = Math.abs(value)
    if (value === undefined) {
      return ''
    }
    if (value > HIG_HIT) {
      return 'hitmap-maker-hig-worn'
    }
    if (value > MID_HIT) {
      return 'hitmap-maker-mid-worn'
    }
    if (value > LOW_HIT) {
      return 'hitmap-maker-low-worn'
    }
    return ''
  }

  function hitClassPoint(row: MapRow): string {
    const value = Math.max(Math.abs(row.params.hitX), Math.abs(row.params.hitY), Math.abs(row.params.hitZ))
    if (value > HIG_HIT) {
      return 'rgba(247, 25, 0, 0.2)'
    }
    if (value > MID_HIT) {
      return 'rgba(247, 127, 0, 0.2)'
    }
    if (value > LOW_HIT) {
      return 'rgba(255, 215, 0, 0.2)'
    }
    return 'rgba(0, 0, 0, 0.2)'
  }

  return (
    <MapContainer attributionControl={false} center={center} zoom={13} style={{ height: '100%', width: '100%' }} preferCanvas={true}>
      <TileLayer
        attribution='evoltag.pl'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {map.rows?.map((row, i) => (
        <CircleMarker
          key={i}
          center={row}
          radius={12} // Adjust this to match your visual size
          pathOptions={{
            fillColor: hitClassPoint(row), // or derive from your hitClassPoint()
            color: '#000',
            weight: 1,
            opacity: 1,
            fillOpacity: 0.8,
          }}
        >
          <Popup>
            <div onClick={() => row.machine.id && navigate(`/communal/machine/${row.machine.id}`)}>

              {!row.machine.id ? <p style={{ cursor: 'pointer' }} onClick={() => session.user.role === Roles.SuperAdmin && openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{`IMEI: ${row.imei}`}</p> : null}
              {row.machine.id ? <p style={{ cursor: 'pointer' }}>{row.machine.name}</p> : null}
              <p>{session.translates.for('map.hit_time')}: {formatDate(new Date(row.time))}</p>
              {/* <p>{session.translates.for('map.speed')}: {row.speed} km/h</p> */}
              <p>{session.translates.for('map.direction')}: {row.angle}°</p>
              <p>{session.translates.for('map.ucid')}: {row.params.ucid ? row.params.ucid : '-'}</p>
              <p className={hitClassColor(row?.params?.hitX)}>{session.translates.for('map.hit_x')}: {(row.params.hitX).toFixed(2)} FV</p>
              <p className={hitClassColor(row?.params?.hitY)}>{session.translates.for('map.hit_y')}: {(row.params.hitY).toFixed(2)} FV</p>
              {/* <p className={hitClassColor(row?.params?.hitZ)}>{session.translates.for('map.hit_z')}: {(row.params.hitZ).toFixed(2)} FV</p> */}
              {session.user.role === Roles.SuperAdmin && !row.machine.id ? <p className='main-btn' onClick={() => openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{session.translates.for('map.add_btn')}</p> : null}

              {row.machine.id ? <p className='main-btn' onClick={() => navigate(`/communal/machine/${row.machine.id}`)}>{session.translates.for('map.machine_page')}</p> : null}
            </div>
          </Popup>
        </CircleMarker>
      ))}

      {myposition ? <Marker
        key='my-location'
        position={myposition}
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px; margin: -16px auto auto 0px" />`,
          iconSize: [32, 32], // Adjust size as needed
          iconAnchor: [16, 16], // Anchor point
        })}></Marker> : null}
    </MapContainer>
  )
}

